import React from "react";

import { css, cx } from "@emotion/css";
import Typography from "@mui/material/Typography";
import { Link, RegisteredRouter, ValidateLinkOptions } from "@tanstack/react-router";

import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Dot } from "@/uiKit/atoms/Dot.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

// Effectively a pill: large enough that any reasonable element renders fully rounded.
const PILL_BORDER_RADIUS_PX = 10000;

export type BaseTabProps<TOptions = unknown, TDefaultFrom extends string = string, TComp = "a"> = {
  label: string;
  selected: boolean;
  value: string;
  color?: PaletteColors;
  count?: number;
  /** Shows a notification dot (`${id}-dot`) in the tab's top-right corner. */
  notify?: boolean;
  link: ValidateLinkOptions<RegisteredRouter, TOptions, TDefaultFrom, TComp>;
};

export type TabProps<TOptions = unknown, TDefaultFrom extends string = string> = AtomProps<
  BaseTabProps<TOptions, TDefaultFrom>,
  StyleClasses<"root">
>;

export const Tab = <TOptions = unknown, TDefaultFrom extends string = string>({
  id,
  label,
  selected,
  color = "orange" as PaletteColors,
  count,
  notify = false,
  link,
  className,
  classes,
  ...props
}: TabProps<TOptions, TDefaultFrom>): React.ReactElement => {
  const styles = useStyles(makeStyles, color);

  return (
    <Link
      id={id}
      className={cx(
        {
          [styles.root]: true,
          [styles.selected]: selected,
          [styles.notify]: notify,
        },
        className,
      )}
      {...props}
      {...link}
    >
      <Typography variant="body">{label}</Typography>
      {count !== undefined ? (
        <Dot
          color={color}
          className={cx({
            [styles.count]: true,
            [styles.countSelected]: selected,
            selected: selected,
          })}
          content={<Typography variant="bodySmall">{count}</Typography>}
        />
      ) : undefined}
      {notify && <Dot id={suffixId(id, "dot")} className={styles.notificationDot} color="blue" size="zero" />}
    </Link>
  );
};

const makeStyles = (theme: Theme, color: PaletteColors) => ({
  notify: css({
    position: "relative",
  }),
  notificationDot: css({
    position: "absolute",
    top: theme.spacing(3),
    right: theme.spacing(3),
  }),
  root: css({
    cursor: "pointer",
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    alignItems: "center",
  }),
  selected: css({
    backgroundColor: theme.palette[color].contrastText,
    borderRadius: `${PILL_BORDER_RADIUS_PX}px`,
  }),
  count: css({
    minWidth: theme.spacing(8),
  }),
  countSelected: css({
    backgroundColor: theme.palette.common.white,
  }),
});
